import { useState, useEffect, ChangeEvent, useMemo } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  IconButton,
  InputAdornment,
  TextField,
  Avatar,
  Typography,
  Pagination,
  Skeleton,
  Stack,
  Tooltip,
  Button,
  Autocomplete,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
  FormControl,
  InputLabel,
  Chip,
} from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import { CheckBox } from '@mui/icons-material';
// import { useNavigate } from "react-router-dom";
import { debounce, findIndex } from 'lodash';
import { truncateText } from '../../common/helper';
import { ListTableWrapper, AntSwitch } from './ListTable.styled';
import { TableColumn, TableData } from './type';
import { useCheckPermissions } from '../../utils/roles/permissionUtils';
import FileUploadModal from '../../modules/GrowthReport/FileUploadModal';
import { Dropdown } from '../Dropdown';
import { getcurrentFinancialYear } from '../../utils/CommHandler';
import { useFinancialYearContext } from '../../modules/Eligibility/FinancialYearContext';

const START_FINANCIAL_YEAR = 2022;

interface ListTableProps {
  tabValue: string;
  columns: TableColumn[];
  data: TableData[];
  tableName?: string;
  isSubmitClicked?: boolean;
  downloadCsv?: any;
  downloadCsvReport?: any;
  handleEdit?: () => void;
  actions?: {
    icon?: React.ReactNode;
    label: string;
    color: string;
    onClick: (item: TableData) => void;
    disabled?: boolean;
  }[];
  itemsPerPage?: number;
  onActionClick?: (action: any, item: TableData) => void;
  isStatus?: boolean;
  timeout: number;
  renderRow?: (item: TableData, index: number, searchQuery: any) => React.ReactNode;
  heading?: string;
  buttonText?: string;
  selectedYear?: string;
  currentFinancialYear?: string;
  tableData?: TableData;
  isDraft?: boolean;
  handleEditButton?: (item: TableData) => void;
  isEditMode?: boolean;
  onChange?: any;
  buOptions?: string[];
  handleAddNew?: any;
  isCancelClicked?: boolean;
  designationValue?: any;
  designationOnChange?: any;
  designationOptions?: any;
  setLoadingState?: (loading: boolean) => void; // Define the prop type
  isEditDisabled?: boolean;
}

type CategoryOption = {
  id: number;
  label: string;
  optionValue: number;
};

const ListTable = ({
  tabValue,
  columns,
  data,
  tableName,
  isSubmitClicked,
  downloadCsv,
  downloadCsvReport,
  isCancelClicked,
  handleEdit,
  handleEditButton,
  isEditMode,
  actions,
  itemsPerPage = 10,
  onActionClick,
  isStatus = true,
  timeout,
  renderRow,
  heading,
  buttonText,
  selectedYear,
  currentFinancialYear,
  tableData,
  isDraft,
  onChange,
  buOptions = [],
  designationValue = [],
  designationOnChange = () => undefined,
  designationOptions = [],
  handleAddNew,
  setLoadingState,
  isEditDisabled,
}: ListTableProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [filterDepartment, setFilterDepartment] = useState<string[]>([]);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const hasEEPermission = useCheckPermissions('ADELG', ['Edit']);
  const hasAAPermission = useCheckPermissions('ADAPPRAISAL', ['Edit']);
  const hasDSGNPermission = useCheckPermissions('ADDSGN', ['Edit']);
  const hasSlabPermission = useCheckPermissions('ADSLM', ['Edit']);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const financialYear = currentMonth >= 3 ? currentYear : currentYear - 1;
  const categoryList = useMemo<CategoryOption[]>(() => {
    const options: CategoryOption[] = [];
    for (let year = START_FINANCIAL_YEAR; year <= financialYear; year += 1) {
      options.push({ id: year, label: `${year}-${year + 1}`, optionValue: year });
    }
    return options;
  }, [financialYear]);

  const { setFinancialYear, notMatchingFinancialYear, SetNotMatchingFinancialYear } = useFinancialYearContext();
  const [selectedCategory, setSelectedCategory] = useState(() => {
    const defaultYear = getcurrentFinancialYear();
    const defaultCategory = categoryList.find((item) => item.label === defaultYear);
    return defaultCategory ? defaultCategory.label : '';
  });
  const debouncedSearch = useMemo(
    () =>
      debounce((searchValue: string) => {
        setDebouncedSearchQuery(searchValue);
      }, 500),
    [],
  );
  useEffect(() => {
    setLoadingState?.(isLoading);
  }, [isLoading, setLoadingState]);

  useEffect(() => {
    debouncedSearch(searchQuery);
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchQuery, debouncedSearch]);

  useEffect(() => {
    setSelectedDepartment('');
  }, [tableName]);

  useEffect(() => {
    if (tableName === 'Career-level Appraisal') {
      setSearchQuery('');
      setDebouncedSearchQuery('');
    } else if (tableName === 'Budget Details' && (searchQuery !== '' || debouncedSearchQuery !== '')) {
      setSearchQuery('');
      setDebouncedSearchQuery('');
    }
  }, [tableName, searchQuery, debouncedSearchQuery]);
  const handleChange = (e: any) => {
    const selectedId = e.target.value;
    const numericSelectedId = Number(selectedId);
    const selectedOption = categoryList.find((item) => item.optionValue === numericSelectedId);
    if (!selectedOption) {
      setSelectedCategory('');
      return;
    }
    setSelectedCategory(selectedOption.label);
    setFinancialYear({
      year: selectedOption.label,
      value: selectedOption.optionValue,
    });
    const flag = getcurrentFinancialYear() !== selectedOption.label;
    SetNotMatchingFinancialYear(flag);
  };
  const handleTextFieldChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, elementId: number, columnAccessor: string) => {
    const newValue = e.target.value;
    const maxValue = tableName === 'Slab Mapping' ? 150 : 100;

    const actualIndex = findIndex(tableData as TableData[], (item) => item.id === elementId);
    if (actualIndex >= 0 && actualIndex < (tableData as TableData[]).length) {
      const newTableData = [...(tableData as TableData[])];
      const accessorParts = columnAccessor.split('.');
      let targetObject = newTableData[actualIndex];
      for (let i = 0; i < accessorParts.length - 1; i += 1) {
        const part = accessorParts[i];
        if (!targetObject[part]) {
          targetObject[part] = {};
        }
        targetObject = targetObject[part];
      }
      const parsedValue = parseFloat(newValue);
      if (Number.isNaN(parsedValue) || parsedValue < 0 || parsedValue > maxValue) {
        if (parsedValue > maxValue) {
          console.warn(`You cannot add more than ${maxValue}%`);
          return;
        }
      }
      targetObject[accessorParts[accessorParts.length - 1]] = newValue === '' ? '' : parsedValue;
      onChange(newTableData);
    } else {
      console.error('Index out of bounds:', actualIndex);
    }
  };

  useEffect(() => {
    if (heading === 'Eligibility' && searchQuery === '' && debouncedSearchQuery !== '' && filterDepartment.length === 0) {
      setDebouncedSearchQuery('');
      setCurrentPage(1);
      setSelectedDepartment('');
    } else if (heading !== 'Eligibility' && searchQuery === '' && debouncedSearchQuery !== '' && filterDepartment.length === 0) {
      setDebouncedSearchQuery('');
      setCurrentPage(1);
    }
  }, [searchQuery, debouncedSearchQuery, filterDepartment.length, heading]);

  const getValue = (item: any, column: any) => {
    if (column.accessor.includes('.')) {
      const accessorParts = column.accessor.split('.');
      if (accessorParts[0] === 'budget_details') {
        return item.budget_details?.[accessorParts[1]] ?? 0;
      }
      return item[accessorParts[0]]?.[accessorParts[1]] ?? 0;
    }
    return item[column.accessor] ?? 0;
  };
  const filteredData = isLoading
    ? new Array(itemsPerPage).fill(null)
    : data
        ?.filter((item) => {
          if (selectedDepartment && (item.buHead || item.department) !== selectedDepartment) {
            return false;
          }
          if (heading === 'Eligibility') {
            const lowerCaseSearchQuery = debouncedSearchQuery.toLowerCase();
            const hasMatchingEmployee = item.employees.some((employee: any) => {
              const employeeName = employee?.fullName?.toString().toLowerCase() || '';
              return employeeName.includes(lowerCaseSearchQuery);
            });
            return hasMatchingEmployee;
          }
          return columns.some((column) => {
            const columnAccessor = column?.accessor;
            const columnValue = item && columnAccessor ? item[columnAccessor] : undefined;
            if (columnValue !== undefined && columnValue !== null) {
              const lowerCaseColumnValue = columnValue.toString().toLowerCase();
              const lowerCaseSearchQuery = debouncedSearchQuery.toLowerCase();
              return lowerCaseColumnValue.includes(lowerCaseSearchQuery);
            }
            return false;
          });
        })
        .filter((item) => {
          if (filterDepartment.length === 0) {
            return true;
          }
          return filterDepartment.includes(item.department || item.buHead);
        });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const visibleData = filteredData.slice(startIndex, endIndex);
  const totalRecords = filteredData?.length;
  useEffect(() => {
    if (currentPage > totalPages && totalPages !== 0) {
      setCurrentPage(totalPages);
    }

    if (visibleData.length === 0 && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }, [filteredData, currentPage, totalPages, visibleData]);

  useEffect(() => {
    setLoadingState?.(true);

    const timer = setTimeout(() => {
      setIsLoading(false);
      setLoadingState?.(false);
    }, timeout);

    return () => clearTimeout(timer);
  }, [timeout, currentPage, setLoadingState]);

  const departmentOptions = data
    ?.map((department) => ({
      value: department.department,
      label: department?.department,
    }))
    .filter((option) => option.value !== undefined);

  const handleDepartmentChange = (event: any) => {
    setFilterDepartment(event.target.value as string[]);
  };

  const formatTextValue = (text: any): string | any[] => {
    if (Array.isArray(text) || typeof text === 'string') {
      return text;
    }
    if (text === null || typeof text === 'undefined') {
      return '';
    }
    return String(text);
  };

  const sanitizeHtml = (value: any) => {
    const raw = formatTextValue(value);
    if (Array.isArray(raw)) {
      return raw.map((item) => (typeof item === 'string' ? item.replace(/<[^>]+>/g, ' ') : item));
    }
    return raw.replace(/<[^>]+>/g, ' ');
  };

  const getHtmlSnippetKey = (snippet: any, baseKey: string) => {
    if (typeof snippet === 'string') {
      return `${baseKey}-${snippet.slice(0, 30)}`;
    }
    try {
      return `${baseKey}-${JSON.stringify(snippet).slice(0, 30)}`;
    } catch {
      return baseKey;
    }
  };

  const renderHtmlContent = (content: any, baseKey: string) => {
    if (Array.isArray(content)) {
      return content.map((htmlSnippet) => {
        const sanitized = sanitizeHtml(htmlSnippet);
        const snippetKey = getHtmlSnippetKey(sanitized, baseKey);
        return <div key={snippetKey}>{sanitized}</div>;
      });
    }
    return <span>{sanitizeHtml(content)}</span>;
  };

  const getRowKey = (rowItem: TableData, index: number) => rowItem.id ?? rowItem.employee_code ?? rowItem.employee_name ?? `row-${index}`;

  const renderDefaultText = (text: any, truncateLength = 20, align: 'left' | 'center' = 'left') => (
    <ListItemText className="listIem-text" sx={align === 'center' ? { textAlign: 'center' } : undefined} primary={truncateText(formatTextValue(text), truncateLength)} />
  );

  const renderNumberField = (fieldValue: number, rowItem: TableData, accessor: string, options?: { disabled?: boolean; width?: number | string; align?: 'left' | 'center' }) => {
    const { disabled = false, width, align = 'center' } = options || {};
    return (
      <TextField
        value={fieldValue}
        onChange={(event) => {
          if (!disabled) {
            handleTextFieldChange(event, rowItem?.id, accessor);
          }
        }}
        size="small"
        variant="outlined"
        disabled={disabled}
        sx={width ? { width } : undefined}
        inputProps={{
          style: { textAlign: align },
        }}
      />
    );
  };

  const renderBudgetPercentageField = (fieldValue: number, rowItem: TableData, accessor: string) => {
    if (selectedYear !== currentFinancialYear) {
      return renderDefaultText(fieldValue, 20, 'center');
    }
    if (isDraft) {
      return renderNumberField(fieldValue, rowItem, accessor, { disabled: true });
    }
    return renderNumberField(fieldValue, rowItem, accessor);
  };

  const renderAppraisalField = (fieldValue: number, rowItem: TableData, accessor: string) => {
    if (selectedYear !== currentFinancialYear) {
      return (
        <ListItemText
          className="listIem-text"
          sx={{
            textAlign: 'left',
            margin: '40px',
          }}
          primary={truncateText(formatTextValue(fieldValue), 20)}
        />
      );
    }
    if (isDraft) {
      return renderNumberField(fieldValue, rowItem, accessor, { disabled: true });
    }
    return renderNumberField(fieldValue, rowItem, accessor);
  };

  const renderSlabScoreField = (rowItem: TableData, accessor: string) => {
    if (isEditMode) {
      return renderNumberField(rowItem.slab_mapping?.score ?? 0, rowItem, accessor, { width: '80px' });
    }
    return <ListItemText className="listIem-text" primary={rowItem.slab_mapping?.score ?? 0} />;
  };

  const renderNameField = (rowItem: TableData) => {
    const showActions = Array.isArray(actions) && actions.length > 0;
    return (
      <ListItemText
        className="listIem-text"
        primary={
          <>
            {showActions ? truncateText(rowItem?.name, 20) : rowItem?.name}
            {rowItem?.specialSubCategory && (
              <Typography component="span" style={{ color: 'red', fontSize: '20px' }}>
                {' *'}
              </Typography>
            )}
          </>
        }
      />
    );
  };

  const renderResponsibilityField = (rowItem: TableData) => renderDefaultText(sanitizeHtml(rowItem?.responsibilites), 20);

  const renderStatusToggle = (rowItem: TableData) => {
    if (!(isStatus && hasDSGNPermission)) {
      return null;
    }
    return (
      <Box className="status-field-wrap" m={2}>
        <Stack direction="row" spacing={1} alignItems="center" style={{ marginLeft: '15px' }}>
          <AntSwitch
            checked={rowItem.status === 1 || rowItem.status === true}
            inputProps={{ 'aria-label': 'ant design' }}
            onClick={() => {
              if (onActionClick) {
                onActionClick({ label: 'status' }, rowItem);
              }
            }}
          />
        </Stack>
      </Box>
    );
  };

  const renderActions = (rowItem: TableData) => {
    if (!actions || actions.length === 0 || tableName === 'Budget Details' || tableName === 'Career-level Appraisal') {
      return null;
    }

    return (
      <Box className="actions-field-wrap">
        {actions.map((action) => {
          const combinedAction = {
            ...action,
            disabled: action?.disabled && !isSubmitClicked,
          };
          const actionKey = `${combinedAction.label || 'action'}-${rowItem.id || rowItem.employee_code || rowItem.employee_name || 'row'}`;
          return (
            <span key={actionKey}>
              <Tooltip title={combinedAction.label} arrow>
                <IconButton
                  size="large"
                  className={combinedAction?.color ? combinedAction?.color : 'primary-icon'}
                  onClick={() => {
                    if (onActionClick) {
                      onActionClick(combinedAction, rowItem);
                    }
                  }}
                  disabled={combinedAction?.disabled}
                >
                  {combinedAction?.icon}
                </IconButton>
              </Tooltip>
            </span>
          );
        })}
      </Box>
    );
  };

  const skeletonPlaceholders = useMemo(() => Array.from({ length: Math.max(1, Math.floor(itemsPerPage / 2)) }, (_, idx) => `skeleton-row-${idx}`), [itemsPerPage]);

  const renderSkeletonRows = () =>
    skeletonPlaceholders.map((placeholderKey) => (
      <div className="table-row-item" key={placeholderKey}>
        {columns.map((column) => (
          <Box className="list-list_table-box-wrap" key={`${placeholderKey}-${column?.accessor}`}>
            <List>
              <ListItem className="list-list_table">
                <Skeleton width={300} animation="wave" variant="text" />
              </ListItem>
            </List>
          </Box>
        ))}
        {isStatus && hasDSGNPermission && (
          <Box className="list-list_table-box-wrap" key={`${placeholderKey}-status`}>
            <List>
              <ListItem className="list-list_table">
                <Skeleton width={150} animation="wave" variant="text" />
              </ListItem>
            </List>
          </Box>
        )}
        {Array.isArray(actions) && actions.length !== 0 && (
          <Box className="list-list_table-box-wrap" key={`${placeholderKey}-actions`}>
            <List>
              <ListItem className="list-list_table">
                <Skeleton width={150} animation="wave" variant="text" />
              </ListItem>
            </List>
          </Box>
        )}
      </div>
    ));

  const renderTableRows = () => {
    if (isLoading) {
      return renderSkeletonRows();
    }

    if (!visibleData || visibleData.length === 0) {
      return <p style={{ textAlign: 'center', fontWeight: 400 }}>No data to display</p>;
    }

    if (renderRow) {
      return visibleData.map((rowItem, rowIndex) => renderRow(rowItem, rowIndex, searchQuery));
    }

    return visibleData.map((rowItem, rowIndex) => {
      const rowKey = getRowKey(rowItem, rowIndex);

      return (
        <div className="table-row-item" key={rowKey}>
          {columns.map((column) => {
            const value = getValue(rowItem, column);
            const truncateLength: number = column?.truncate ?? 30;
            const columnAccessorValue = column?.accessor ? (rowItem as any)?.[column?.accessor] : undefined;
            const elem = columnAccessorValue !== undefined ? truncateText(columnAccessorValue, truncateLength) : '';
            let designationDisplay = '';
            if (column?.accessor === 'designations' || column?.accessor === 'designation') {
              const designationField = rowItem[column?.accessor] || rowItem.designation || rowItem.designations;
              if (Array.isArray(designationField)) {
                if (designationField.length > 0 && typeof designationField[0] === 'object') {
                  designationDisplay = designationField.map((desg: any) => desg?.name || desg?.label || '').join(', ');
                } else {
                  designationDisplay = designationField.join(', ');
                }
              } else if (typeof designationField === 'string') {
                designationDisplay = designationField;
              }
            }

            let cellContent: React.ReactNode;
            const columnType = column?.type?.toString().toLowerCase();

            if (columnType === 'image') {
              const imageSource = columnAccessorValue ? decodeURIComponent(columnAccessorValue) : undefined;
              cellContent = <Avatar alt="Image" src={imageSource} />;
            } else if (columnType === 'html') {
              cellContent = renderHtmlContent(elem, `${rowKey}-${column?.accessor}`);
            } else if (column?.accessor === 'designation' || column?.accessor === 'designations') {
              cellContent = renderDefaultText(designationDisplay, 20);
            } else if (column?.accessor === 'budget_details.percentage') {
              cellContent = renderBudgetPercentageField(value, rowItem, column.accessor);
            } else if (column.accessor === 'appraisal_details.non_billable_percentage' || column.accessor === 'appraisal_details.billable_percentage') {
              cellContent = renderAppraisalField(value, rowItem, column.accessor);
            } else if (column?.accessor === 'slab_mapping.score') {
              cellContent = renderSlabScoreField(rowItem, column.accessor);
            } else if (column?.accessor === 'name') {
              cellContent = renderNameField(rowItem);
            } else if (column?.accessor === 'responsibilites') {
              cellContent = renderResponsibilityField(rowItem);
            } else if (column?.accessor === 'AppraisalEligibility' || column?.accessor === 'PromotionEligibility') {
              cellContent = <CheckBox />;
            } else if (column?.accessor === 'category') {
              cellContent = renderDefaultText(rowItem?.category, 20);
            } else if (column?.accessor === 'full_name') {
              cellContent = renderDefaultText(rowItem?.full_name, 40);
            } else if (column?.accessor === 'department') {
              cellContent = renderDefaultText(rowItem?.department, 20);
            } else if (column?.accessor === 'buHead') {
              cellContent = renderDefaultText(rowItem?.buHead, 20);
            } else if (column?.accessor === 'title') {
              cellContent = renderDefaultText(rowItem?.title, 20);
            } else if (column?.accessor === 'hrBPs') {
              cellContent = renderDefaultText(rowItem?.hrBPs, 20);
            } else {
              cellContent = renderDefaultText(columnAccessorValue ?? '', column?.truncate ?? 20);
            }

            return (
              <Box
                className="list-list_table-box-wrap"
                key={`${rowKey}-${column?.accessor}`}
                style={{
                  textAlign: column.accessor === 'percentage' ? 'right' : 'left',
                  marginLeft: column.accessor === 'percentage' ? 'auto' : undefined,
                }}
              >
                <List>
                  <ListItem
                    className={`list-list_table ${tableName === 'Budget Details' ? 'percentage-wrap' : ''}`}
                    sx={{
                      display: tableName === 'Budget Details' ? 'flex' : '',
                      justifyContent: tableName === 'Budget Details' ? 'center' : 'block',
                    }}
                  >
                    {cellContent}
                  </ListItem>
                </List>
              </Box>
            );
          })}
          {renderStatusToggle(rowItem)}
          {heading === 'Users' && renderDefaultText(rowItem?.email, 30, 'left')}
          {renderActions(rowItem)}
        </div>
      );
    });
  };

  return (
    <ListTableWrapper>
      <div className="list_table-main-table">
        <div
          className="table-row-item srch_position"
          style={{
            border: 0,
            marginBottom: heading === 'Eligibility' ? '15px' : '-15px',
          }}
        >
          {tableName === 'Budget Details' && (
            <Typography variant="h6" className="employee-details" component="h6" fontWeight="bold" style={{ flexGrow: 1 }}>
              Budget Details
            </Typography>
          )}
          {tableName === 'Career-level Appraisal' && (
            <Typography variant="h6" className="employee-details" component="h6" fontWeight="bold" style={{ flexGrow: 1 }}>
              Career-level Appraisal
            </Typography>
          )}
          <Stack className="rbac_content_header" direction="row" justifyContent="space-between" flexGrow={1} alignItems="center" mb={2}>
            <Typography variant="h5">{heading}</Typography>
          </Stack>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: '16px', gap: '16px' }}>
            <Stack>
              <TextField
                label={heading === 'Eligibility' ? 'Search Employee' : 'Search'}
                sx={{ marginBottom: '15px' }}
                variant="outlined"
                size="small"
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small">
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                style={{
                  width: '100%',
                  marginTop: heading === 'Eligibility' ? '15px' : undefined,
                }}
              />
            </Stack>
            {heading === 'Designations' && (
              <Autocomplete
                style={{ width: '150px' }}
                value={designationValue}
                onChange={designationOnChange}
                options={designationOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    style={{ bottom: '8px' }}
                    label="Status"
                    variant="outlined"
                    size="small"
                    fullWidth
                    inputProps={{
                      ...params.inputProps,
                      'aria-label': 'Status',
                    }}
                  />
                )}
              />
            )}
            {heading === 'Budget Report' && (
              <>
                <FormControl variant="outlined" style={{ width: '210px' }}>
                  <InputLabel id="multi-select-label">Filter</InputLabel>
                  <Select
                    id="filter-select"
                    multiple
                    value={filterDepartment}
                    onChange={handleDepartmentChange}
                    input={<OutlinedInput label="filter" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', gap: 0.3 }}>
                        {selected.length > 1 ? (
                          <>
                            {selected.slice(0, 1).map((value) => (
                              <Chip key={value} label={value} />
                            ))}
                            <Chip label={`+${selected.length - 1}`} />
                          </>
                        ) : (
                          selected.map((value) => <Chip key={value} label={value} />)
                        )}
                      </Box>
                    )}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 600,
                        },
                      },
                    }}
                  >
                    {buOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        <Checkbox checked={filterDepartment.indexOf(option) > -1} />
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="contained" style={{ bottom: '8px', color: '#ffffff' }} startIcon={<DownloadIcon />} onClick={downloadCsvReport}>
                  Download
                </Button>
              </>
            )}
            <Stack className="rbac_content_header" direction="row" justifyContent="space-between" flexGrow={1} alignItems="center" mb={2}>
              {heading === 'Growth Report' ? (
                <FileUploadModal />
              ) : (
                buttonText && (
                  <div
                    style={{
                      display: 'flex',
                      paddingRight: '1rem',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: 'max-Content',
                        color: '#ffffff',
                      }}
                      variant="contained"
                      // className='cs_btn_type'
                      size="large"
                      onClick={handleAddNew}
                    >
                      {buttonText}
                    </Button>
                  </div>
                )
              )}
            </Stack>
            {heading === 'Eligibility' && (
              <>
                <FormControl sx={{ width: '210px' }}>
                  <InputLabel id="demo-multiple-checkbox-label">Filter</InputLabel>
                  <Select
                    id="filter-select"
                    multiple
                    style={{ marginTop: '3px', height: '38px' }}
                    value={filterDepartment}
                    onChange={handleDepartmentChange}
                    input={<OutlinedInput label="filter" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', gap: 0.3 }}>
                        {selected.length > 1 ? (
                          <>
                            {selected.slice(0, 1).map((value) => (
                              <Chip key={value} label={value} />
                            ))}
                            <Chip label={`+${selected.length - 1}`} />
                          </>
                        ) : (
                          selected.map((value) => <Chip key={value} label={value} />)
                        )}
                      </Box>
                    )}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 200,
                        },
                      },
                    }}
                  >
                    {departmentOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Checkbox checked={filterDepartment.includes(option.value)} />
                        <ListItemText primary={option.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {hasEEPermission && (
                  <Button variant="contained" disabled={notMatchingFinancialYear ? true : !isSubmitClicked && !isCancelClicked} onClick={handleEdit} sx={{ color: '#ffffff' }}>
                    Edit
                  </Button>
                )}
                <Button variant="contained" sx={{ color: '#ffffff' }} startIcon={<DownloadIcon />} onClick={downloadCsv}>
                  Download
                </Button>
                <Dropdown inputLabel="Select Category*" value={selectedCategory} options={categoryList} disabled={false} onChange={handleChange} padding="0" />
              </>
            )}
            {tableName === 'Budget Details' && (
              <>
                <Autocomplete
                  id="filter-select"
                  style={{ width: '195px' }}
                  options={data.map((item) => item.department)}
                  getOptionLabel={(option) => option}
                  // style={{ marginTop: "-15px", width: "100px" }}
                  onChange={(_event, newValue) => setSelectedDepartment(newValue)}
                  renderInput={(params) => <TextField style={{ bottom: '8px' }} {...params} label="Filter" variant="outlined" size="small" fullWidth />}
                />
                {hasAAPermission && (
                  <Button variant="contained" disabled={isEditMode || selectedYear !== currentFinancialYear} onClick={handleEditButton} style={{ marginTop: '-15px', color: '#ffffff' }}>
                    Edit
                  </Button>
                )}
              </>
            )}
            {tableName === 'Career-level Appraisal' && (
              <>
                {hasAAPermission && (
                  <Button variant="contained" disabled={!isDraft || selectedYear !== currentFinancialYear || isEditDisabled} onClick={handleEditButton} style={{ marginTop: '-15px' }}>
                    Edit
                  </Button>
                )}
                {/* <Autocomplete
                  id="filter-select"
                  options={data?.map((item) => item?.name)}
                  getOptionLabel={(option) => option}
                  style={{ marginTop: "-15px", width: "100px" }}
                  onChange={(_event, newValue) =>
                    setSelectedDepartment(newValue)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filter"
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                  )}
                /> */}
              </>
            )}
            {tableName === 'Slab Mapping' && hasSlabPermission && (
              <Button variant="contained" onClick={handleEditButton} style={{ marginTop: '-15px' }}>
                Edit
              </Button>
            )}
          </Box>
        </div>
        <TabContext value={tabValue}>
          <TabPanel value={tabValue} className="override-tabpanel">
            <div className="table-row-item-cell table-header-modify">
              {columns.map((column) => (
                <Box
                  key={column?.accessor}
                  className="list_table-table-head"
                  style={{
                    textAlign: column.accessor === 'budget_details.percentage' || column.accessor === 'employee_email' ? 'center' : 'left',
                  }}
                >
                  {column.header}
                </Box>
              ))}
              {isStatus && hasDSGNPermission && <Box className="list_table-table-head list-table-status-head">Status</Box>}
              {Array.isArray(actions) && actions.length !== 0 && (
                <Box className="list_table-table-head list-table-action-head" style={heading === 'Users' ? { position: 'relative', left: '6%' } : {}}>
                  Actions
                </Box>
              )}
            </div>
            {renderTableRows()}
          </TabPanel>
        </TabContext>
      </div>
      <Box mt={2} display="flex" justifyContent="flex-end">
        <div className="pgntn_box">
          <Typography className="showing_rslt" variant="body2" color="textSecondary">
            Showing {visibleData && visibleData?.length} records out of {totalRecords} total.
          </Typography>
          <Pagination count={totalPages} page={currentPage} onChange={(event, page) => setCurrentPage(page)} color="primary" size="large" className="pagination cs_pgntn" siblingCount={1} boundaryCount={1} showFirstButton showLastButton />
        </div>
      </Box>
    </ListTableWrapper>
  );
};

ListTable.defaultProps = {
  tableName: undefined,
  isSubmitClicked: false,
  itemsPerPage: 10,
  isStatus: true,
  downloadCsv: undefined,
  downloadCsvReport: undefined,
  handleEdit: undefined,
  actions: [],
  onActionClick: undefined,
  renderRow: undefined,
  heading: undefined,
  buttonText: undefined,
  selectedYear: undefined,
  currentFinancialYear: undefined,
  tableData: undefined,
  isDraft: undefined,
  handleEditButton: undefined,
  isEditMode: undefined,
  onChange: undefined,
  buOptions: [],
  designationValue: [],
  designationOptions: [],
  handleAddNew: undefined,
  isCancelClicked: undefined,
  designationOnChange: undefined,
  setLoadingState: undefined,
  isEditDisabled: undefined,
};

export default ListTable;
